import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import { getStateOptions } from '../../utils';
import { IError } from '../BusinessInfo';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    formatPhone,
    formatPostalCode_CA,
    formatPostalCode_US,
} from 'src/utils/formatInputs';
import { NUMBER_FIVE, NUMBER_ZERO } from 'src/utils/constants';
import React, { useState } from 'react';

interface IProps {
    handlerEdit: Function;
    object: {
        addressLine1: string;
        addressLine2: string;
        city: string;
        province: string;
        postalCode: string;
        telephone: string;
    };
    errors: {
        addressLine1: IError;
        addressLine2: IError;
        city: IError;
        province: IError;
        postalCode: IError;
        telephone: IError;
    };
    disabled: boolean;
}

const BusinessComponent = ({
    handlerEdit,
    object,
    errors,
    disabled,
}: IProps) => {
    const { businessComercial, occupationIndividualInformation } =
        useLocalization();
    const { markCreditFormTouched } = useCreditForm();
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const [oldPhone, setOldPhone] = useState<string>('');
    const propertyName = 'business';
    const isCanada =
        dealerDataInfo?.dealer.country === 'Canada' ||
        dealerDataInfo?.dealer.country === 'CA';
    const stateOptions = getStateOptions(
        isCanada,
        occupationIndividualInformation,
    );
    const postalcodeMaxLen = isCanada ? Number('7') : Number('5');
    const postalcodeVal = !isCanada
        ? object.postalCode.slice(NUMBER_ZERO, NUMBER_FIVE)
        : object.postalCode;

    const handlePostalChange = (e: any) => {
        let value = e.target.value.trim().toUpperCase();
        if (value.length > postalcodeVal.length) {
            if (!isCanada) {
                value = formatPostalCode_US(value);
            } else {
                value = formatPostalCode_CA(value);
            }
        }
        handlerEdit(
            {
                name: 'postalCode',
                value,
            },
            propertyName,
        );
    };

    const validatePhone = (e: any) => {
        let aux: string = e.target.value
            .split('(')
            .join()
            .split(')')
            .join()
            .split('-')
            .join();
        aux = aux.replaceAll(',', '').replaceAll(' ', '');
        if (/^[0-9]*$/.test(aux)) {
            let newValue = '';
            if (e.target.value.length > oldPhone.length) {
                newValue = formatPhone(e.target.value);
            } else {
                newValue = e.target.value;
            }
            handlerEdit(
                {
                    name: e.target.name,
                    value: newValue,
                },
                propertyName,
            );
            setOldPhone(e.target.value);
        }
    };
    return (
        <div className={'business-business u-mt-large'}>
            <span className='c-form-section__header-content'>
                <span className='c-form-section__title  js-form-section__trigger'>
                    <span
                        className='c-form-section__title-text form-section__title-font'
                        role='heading'
                    >
                        {businessComercial.BUSINESS_TITLE}
                    </span>
                </span>
            </span>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.BUSINESS_ADDRESS1}
                            type={'text'}
                            name={'addressLine1'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            maxlength={40}
                            value={object.addressLine1}
                            asTouched={errors.addressLine1.error}
                            asErrors={errors.addressLine1.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.BUSINESS_ADDRESS2}
                            type={'text'}
                            name={'addressLine2'}
                            maxlength={25}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.addressLine2}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.BUSINESS_CITY}
                            type={'text'}
                            name={'city'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            maxlength={30}
                            value={object.city}
                            asTouched={errors.city.error}
                            asErrors={errors.city.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {businessComercial.BUSINESS_PROVINCE}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'optionBusinessProvince'}
                                        options={stateOptions}
                                        defaultValue={object.province}
                                        placeholder={
                                            businessComercial.APPLICANT_CHOOSEPROVINCE
                                        }
                                        selectedOption={object.province}
                                        onSelect={(value) => {
                                            handlerEdit(
                                                { name: 'province', value },
                                                propertyName,
                                            );
                                        }}
                                        disabled={disabled}
                                    />
                                    {errors.province.error &&
                                        errors.province.msg && (
                                            <span className={'c-error-message'}>
                                                {errors.province.msg}
                                            </span>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.BUSINESS_POSTALCODE}
                            type={'text'}
                            name={'postalCode'}
                            handlerChange={handlePostalChange}
                            maxlength={postalcodeMaxLen}
                            value={postalcodeVal}
                            asTouched={errors.postalCode.error}
                            asErrors={errors.postalCode.msg}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.APPLICANT_TELEPHONE}
                            type={'text'}
                            name={'telephone'}
                            handlerChange={(val: any) => {
                                validatePhone(val);
                                markCreditFormTouched();
                            }}
                            value={object.telephone}
                            maxlength={14}
                            min={0}
                            asTouched={errors.telephone.error}
                            asErrors={errors.telephone.msg}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessComponent;
